import * as React from 'react';
import { Bar, CartesianGrid, ComposedChart, ReferenceLine, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { cx } from '../../../utils/cx';
import {
  formatChartPeriodLong,
  formatChartPeriodShort,
  formatCurrencyWhole,
  formatMoneyShort,
} from '../../../utils/formatting';
import AnimationFixToolTip from '../../base/AnimationFixToolTip';
import { CHART_NO_FOCUS_OUTLINE, DEFAULT_CHART_HEIGHT } from '../../base/charts/ChartsBase';
import LoadingIndicator from '../../base/LoadingIndicator';
import { useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';
import { calculateLabelInterval, getDateRangeForPeriod, getTransactionGroupType, TransactionGroupType } from '../utils';

import { processSpendingChartData } from './dataProcessing';
import { SpendingChartHeader } from './SpendingChartHeader';

import type { ColumnTable } from 'arquero';

/**
 * Custom tooltip content for SpendingChart that matches Figma design
 * Shows bullet points for Income and Spending, and Net amount without bullet
 */
const SpendingChartTooltipContent = ({
  active,
  payload,
  labelFormatter,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: ISpendingChartDataPoint;
  }>;
  labelFormatter?: (value: string) => string;
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Find the data point
  const dataPoint = payload[0]?.payload;
  if (!dataPoint) {
    return null;
  }

  // Format the label (date)
  const label = labelFormatter ? labelFormatter(dataPoint.period) : dataPoint.period;

  const incomeFormatted = formatCurrencyWhole(dataPoint.income);
  const spendingFormatted = formatCurrencyWhole(dataPoint.spending);
  const netAmountFormatted = `${dataPoint.netAmount >= 0 ? '+' : ''}${formatCurrencyWhole(dataPoint.netAmount)}`;
  const isNetPositive = dataPoint.netAmount >= 0;

  return (
    <div className="bg-primary-solid flex flex-col gap-0.5 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-utility-gray-300 text-[9px] font-semibold">{label}</p>
      <div className="flex flex-col gap-0.5">
        {/* Income */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <div className="bg-utility-indigo-500 h-1.5 w-1.5" />
            <p className="text-tooltip-supporting-text text-xs font-semibold">Inflow</p>
          </div>
          <p className="text-tooltip-supporting-text text-right text-xs font-semibold">{incomeFormatted}</p>
        </div>
        {/* Spending */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <div className="bg-utility-indigo-300 h-1.5 w-1.5" />
            <p className="text-tooltip-supporting-text text-xs font-semibold">Spending</p>
          </div>
          <p className="text-tooltip-supporting-text text-right text-xs font-semibold">{spendingFormatted}</p>
        </div>
        {/* Divider */}
        <div className="my-0.5 border-t border-white/20" />
        {/* Net amount */}
        <div className="flex items-center justify-between gap-4">
          <p className="text-tooltip-supporting-text text-xs font-semibold">Net amount</p>
          <p
            className={`text-right text-xs font-semibold ${
              isNetPositive ? 'text-utility-success-500' : 'text-utility-error-500'
            }`}
          >
            {netAmountFormatted}
          </p>
        </div>
      </div>
    </div>
  );
};

export interface ISpendingChartDataPoint {
  period: string; // YYYY-MM-DD format
  income: number;
  spending: number;
  spendingNegative: number; // Pre-calculated negative value for chart display
  spacer: number; // Transparent spacer to reset stack to zero
  netAmount: number;
  pointY?: number; // Y position for the point overlay
}

export interface ISpendingChartProps {
  transactions: ColumnTable | null;
  dateRange: CalendarDateRange;
  onDateRangeChange?: (dateRange: CalendarDateRange) => void;
  className?: string;
  isLoading?: boolean;
}

const SpendingChart = ({
  transactions,
  dateRange,
  onDateRangeChange,
  className = '',
  isLoading = false,
}: ISpendingChartProps) => {
  const { containerWidth, containerRef } = useContainerWidth();

  // Get the display text for the selected period
  const periodText = useDateRangeDisplayText(dateRange);

  // Determine how to group transactions (day, month, or year)
  const groupType = React.useMemo(() => getTransactionGroupType(dateRange), [dateRange]);

  // Process the data
  const chartData = React.useMemo(() => {
    if (!transactions) {
      return [];
    }

    // dateRange already contains CalendarDate objects
    const startDate = dateRange.start;
    const endDate = dateRange.end;

    return processSpendingChartData({
      transactions,
      startDate,
      endDate,
      groupType,
    });
  }, [transactions, dateRange, groupType]);

  // Calculate x-axis label interval based on available space
  const xAxisInterval = React.useMemo(() => {
    return calculateLabelInterval({
      numDataPoints: chartData.length,
      containerWidth,
    });
  }, [chartData.length, containerWidth]);

  // Calculate Y-axis domain to center zero
  const yAxisDomain = React.useMemo(() => {
    if (chartData.length === 0) {
      return [-1000, 1000];
    }

    const maxIncome = Math.max(...chartData.map((d) => d.income), 0);
    const maxSpending = Math.max(...chartData.map((d) => d.spending), 0);
    const maxAbsValue = Math.max(maxIncome, maxSpending);

    // Add some padding and ensure symmetric domain around zero
    const padding = maxAbsValue * 0.1;
    const domainMax = Math.ceil(maxAbsValue + padding);
    return [-domainMax, domainMax];
  }, [chartData]);

  // Calculate Y-axis ticks to ensure 0 is always shown
  const yAxisTicks = React.useMemo(() => {
    if (chartData.length === 0) {
      return [-1000, -500, 0, 500, 1000];
    }

    const [domainMin, domainMax] = yAxisDomain;
    const ticks: number[] = [];

    // Generate ticks symmetrically around zero
    const step = Math.ceil((domainMax - domainMin) / 8);
    const roundedStep = Math.max(Math.ceil(step / 100) * 100, 100); // Round to nearest 100, min 100

    // Prevent infinite loops
    if (roundedStep <= 0 || !isFinite(roundedStep)) {
      return [-1000, -500, 0, 500, 1000];
    }

    // Generate negative ticks
    for (let i = 0; i >= domainMin; i -= roundedStep) {
      ticks.push(i);
    }

    // Ensure 0 is included
    if (!ticks.includes(0)) {
      ticks.push(0);
    }

    // Generate positive ticks
    for (let i = roundedStep; i <= domainMax; i += roundedStep) {
      ticks.push(i);
    }

    // Sort and ensure 0 is present
    ticks.sort((a, b) => a - b);
    if (!ticks.includes(0)) {
      ticks.push(0);
      ticks.sort((a, b) => a - b);
    }

    return ticks;
  }, [yAxisDomain, chartData.length]);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (chartData.length === 0) {
      return { netTotal: undefined, income: undefined, spending: undefined };
    }

    const totalIncome = chartData.reduce((sum, d) => sum + d.income, 0);
    const totalSpending = chartData.reduce((sum, d) => sum + d.spending, 0);
    const netTotal = totalIncome - totalSpending;

    return {
      netTotal,
      income: totalIncome,
      spending: totalSpending,
    };
  }, [chartData]);

  const onClickBar = (data: { payload?: { period: string } }) => {
    if (!onDateRangeChange || !data.payload?.period) {
      return;
    }

    const newDateRange = getDateRangeForPeriod(data.payload.period, groupType);
    if (newDateRange) {
      onDateRangeChange(newDateRange);
    }
  };

  const isBarClickable = onDateRangeChange && groupType !== TransactionGroupType.DAY;

  return (
    <div
      ref={containerRef}
      className={cx('bg-secondary_subtle shadow-xs flex shrink-0 flex-col overflow-hidden rounded-2xl', className)}
    >
      {/* Header */}
      <SpendingChartHeader
        title="Net cash flow"
        subtitle={periodText}
        netTotal={summaryStats.netTotal}
        income={summaryStats.income}
        spending={summaryStats.spending}
      />

      {/* Chart Content */}
      <div className="shadow-xs px-5 pb-8 pt-5">
        {isLoading ? (
          <div className="flex items-center justify-center" style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}>
            <LoadingIndicator size="md" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-tertiary flex h-full items-center justify-center">
            No spending data in selected date range
          </div>
        ) : (
          <>
            {/* Legend */}
            <div className="mb-4 flex items-center justify-end gap-3">
              <div className="flex items-center gap-2">
                <div className="bg-utility-indigo-500 h-2 w-2" />
                <p className="text-tertiary text-sm">Inflow</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-utility-indigo-300 h-2 w-2" />
                <p className="text-tertiary text-sm">Spending</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex h-2 w-2 items-center justify-center">
                  <div className="border-utility-gray-400 h-2 w-2 rounded-full border-2 bg-white" />
                </div>
                <p className="text-tertiary text-sm">Net amount</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={DEFAULT_CHART_HEIGHT} className={CHART_NO_FOCUS_OUTLINE}>
              <ComposedChart
                data={chartData}
                margin={{
                  left: -10,
                  right: 20,
                  top: 5,
                  bottom: 5,
                }}
                className="text-tertiary [&_.recharts-text]:text-xs"
              >
                <CartesianGrid
                  vertical={false}
                  horizontal={true}
                  stroke="currentColor"
                  strokeDasharray="0"
                  className="text-utility-gray-100"
                />

                {/* Zero line */}
                <ReferenceLine y={0} stroke="currentColor" strokeWidth={2} className="text-foreground-primary-900" />

                <XAxis
                  fill="currentColor"
                  dataKey="period"
                  axisLine={false}
                  tickLine={false}
                  tickMargin={12}
                  interval={xAxisInterval}
                  tickFormatter={(value: string) => formatChartPeriodShort(value, groupType)}
                />

                <YAxis
                  fill="currentColor"
                  axisLine={false}
                  tickLine={false}
                  tickMargin={8}
                  width={80}
                  domain={yAxisDomain}
                  ticks={yAxisTicks}
                  tickFormatter={(value: number) => formatMoneyShort(value, { precision: 'whole' })}
                />

                <AnimationFixToolTip
                  content={
                    <SpendingChartTooltipContent
                      labelFormatter={(value: string) => formatChartPeriodLong(value, groupType)}
                    />
                  }
                  cursor={{
                    fill: 'transparent',
                  }}
                />

                {/* Income bars - extend upward (positive) */}
                <Bar
                  isAnimationActive={false}
                  dataKey="income"
                  name="Inflow"
                  stackId="cashflow"
                  className={cx('fill-utility-indigo-500', isBarClickable && 'cursor-pointer')}
                  radius={[6, 6, 0, 0]}
                  activeBar={{ className: 'fill-utility-indigo-600' }}
                  onClick={onClickBar}
                />

                {/* Transparent spacer to reset stack to zero */}
                <Bar
                  isAnimationActive={false}
                  dataKey="spacer"
                  name=""
                  stackId="cashflow"
                  fill="transparent"
                  onClick={onClickBar}
                />

                {/* Spending bars - extend downward (negative) */}
                <Bar
                  isAnimationActive={false}
                  dataKey="spendingNegative"
                  name="Spending"
                  stackId="cashflow"
                  className={cx('fill-utility-indigo-300', isBarClickable && 'cursor-pointer')}
                  radius={[6, 6, 0, 0]}
                  activeBar={{ className: 'fill-utility-indigo-400' }}
                  onClick={onClickBar}
                />

                {/* Points overlay showing net amounts - centered in bars */}
                <Scatter
                  isAnimationActive={false}
                  dataKey="pointY"
                  name="Net amount"
                  shape={(props: unknown) => {
                    const p = props as {
                      cx?: number;
                      cy?: number;
                      payload?: ISpendingChartDataPoint;
                    };
                    const cx = p.cx ?? 0;
                    const cy = p.cy ?? 0;
                    if (!p.payload || p.payload.pointY === undefined) {
                      return <circle cx={cx} cy={cy} r={5} fill="transparent" />;
                    }
                    const isPositive = p.payload.netAmount >= 0;
                    const strokeClass = isPositive ? 'stroke-utility-success-500' : 'stroke-utility-error-500';
                    return <circle cx={cx} cy={cy} r={5} className={`fill-white ${strokeClass}`} strokeWidth={2} />;
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
};

export default SpendingChart;
